import React, { useEffect, useState } from 'react';
import API from '../services/api';

// Action modals for the interactive schedule: cancel, reschedule, and the
// shared open-slot picker (dates for a month, then times for a date). All
// slots come from Acuity availability — no manual time entry by design.

const fmtDay = (d) =>
  new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const fmtFull = (iso) =>
  new Date(iso).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export function SlotPicker({ calendarId, typeId, onPick }) {
  const [month, setMonth] = useState(() =>
    new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }).slice(0, 7)
  );
  const [dates, setDates] = useState(null);
  const [date, setDate] = useState(null);
  const [times, setTimes] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setDates(null); setDate(null); setTimes(null); setError('');
    API.get('/acuity/availability', { params: { calendarId, typeId, month } })
      .then((res) => { if (active) setDates(Array.isArray(res.data) ? res.data : []); })
      .catch((err) => { if (active) setError(err.response?.data?.message || 'Could not load open dates.'); });
    return () => { active = false; };
  }, [calendarId, typeId, month]);

  useEffect(() => {
    if (!date) return undefined;
    let active = true;
    setTimes(null); setError('');
    API.get('/acuity/availability', { params: { calendarId, typeId, date } })
      .then((res) => { if (active) setTimes(Array.isArray(res.data) ? res.data : []); })
      .catch((err) => { if (active) setError(err.response?.data?.message || 'Could not load open times.'); });
    return () => { active = false; };
  }, [calendarId, typeId, date]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <button type="button" style={pagerBtn} onClick={() => shiftMonth(-1)}>←</button>
        <span style={{ fontWeight: 700, fontSize: 13 }}>
          {new Date(`${month}-15T12:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        <button type="button" style={pagerBtn} onClick={() => shiftMonth(1)}>→</button>
      </div>
      {error && <div style={errStyle}>{error}</div>}
      {!error && dates === null && <div style={mutedStyle}>Loading open dates…</div>}
      {dates !== null && dates.length === 0 && <div style={mutedStyle}>No openings this month — try the next one.</div>}
      {dates !== null && dates.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {dates.map((d) => (
            <button
              type="button"
              key={d.date}
              onClick={() => setDate(d.date)}
              style={date === d.date ? chipOn : chipOff}
            >
              {fmtDay(d.date)}
            </button>
          ))}
        </div>
      )}
      {date && times === null && <div style={mutedStyle}>Loading open times…</div>}
      {date && times !== null && times.length === 0 && <div style={mutedStyle}>That day just filled up — pick another.</div>}
      {date && times !== null && times.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {times.map((t) => (
            <button type="button" key={t.time} onClick={() => onPick({ date, time: t.time })} style={chipOff}>
              {fmtTime(t.time)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function RescheduleModal({ appt, calendarId, typeId, onDone, onClose }) {
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const clientLabel = `${appt.client_first_name || ''} ${appt.client_last_name || ''}`.trim() || 'this client';

  const confirm = async () => {
    setSubmitting(true); setError('');
    try {
      await API.put(`/acuity/appointments/${appt.acuity_id}/reschedule`, { datetime: picked.time, calendarId });
      onDone();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not reschedule — the slot may have just been taken.');
      setPicked(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Overlay onClose={submitting ? undefined : onClose}>
      <h3 style={titleStyle}>Reschedule — {clientLabel}</h3>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
        Currently {fmtFull(appt.appt_datetime)}
      </div>
      {error && <div style={errStyle}>{error}</div>}
      {!picked ? (
        <SlotPicker calendarId={calendarId} typeId={typeId} onPick={setPicked} />
      ) : (
        <div>
          <div style={{ fontSize: 14, marginBottom: 12 }}>
            Move <strong>{clientLabel}</strong> to <strong>{fmtFull(picked.time)}</strong>?
            They'll receive Acuity's standard reschedule notice.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={btnPrimary} disabled={submitting} onClick={confirm}>
              {submitting ? 'Moving…' : 'Confirm move'}
            </button>
            <button type="button" style={btnGhost} disabled={submitting} onClick={() => setPicked(null)}>
              Pick another time
            </button>
          </div>
        </div>
      )}
    </Overlay>
  );
}

export function CancelConfirmModal({ appt, calendarId, onDone, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const clientLabel = `${appt.client_first_name || ''} ${appt.client_last_name || ''}`.trim() || 'this client';

  const confirm = async () => {
    setSubmitting(true); setError('');
    try {
      await API.put(`/acuity/appointments/${appt.acuity_id}/cancel`, { calendarId });
      onDone();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not cancel — try syncing and retrying.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Overlay onClose={submitting ? undefined : onClose}>
      <h3 style={titleStyle}>Cancel appointment?</h3>
      <div style={{ fontSize: 14, marginBottom: 12 }}>
        Cancel <strong>{clientLabel}</strong>'s {appt.appointment_type || 'appointment'} on{' '}
        <strong>{fmtFull(appt.appt_datetime)}</strong>? They'll receive Acuity's standard
        cancellation notice.
      </div>
      {error && <div style={errStyle}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" style={btnDanger} disabled={submitting} onClick={confirm}>
          {submitting ? 'Canceling…' : 'Yes, cancel it'}
        </button>
        <button type="button" style={btnGhost} disabled={submitting} onClick={onClose}>
          Keep appointment
        </button>
      </div>
    </Overlay>
  );
}

export function Overlay({ children, onClose }) {
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={boxStyle} onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

export function BookModal({ calendarId, onDone, onClose }) {
  const [clients, setClients] = useState(null);
  const [types, setTypes] = useState(null);
  const [query, setQuery] = useState('');
  const [client, setClient] = useState(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [typeId, setTypeId] = useState('');
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    API.get('/acuity/bookable-clients', { params: { calendarId } })
      .then((res) => { if (active) setClients(Array.isArray(res.data) ? res.data : []); })
      .catch((err) => { if (active) setError(err.response?.data?.message || 'Could not load clients.'); });
    API.get('/acuity/appointment-types', { params: { calendarId } })
      .then((res) => { if (active) setTypes(Array.isArray(res.data) ? res.data : []); })
      .catch((err) => { if (active) setError(err.response?.data?.message || 'Could not load appointment types.'); });
    return () => { active = false; };
  }, [calendarId]);

  const pickClient = (c) => {
    setClient(c);
    setEmail(c.email || c.billingEmail || '');
    setPhone(c.phone || '');
  };

  const filtered = (clients || []).filter((c) =>
    c.clientName.toLowerCase().includes(query.trim().toLowerCase())
  ).slice(0, 8);

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      await API.post('/acuity/appointments', {
        calendarId, appointmentTypeId: Number(typeId), datetime: picked.time,
        clientId: client.id, email: email.trim(), phone: phone.trim(),
      });
      onDone();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not book — the slot may have just been taken.');
      setPicked(null);
    } finally {
      setSubmitting(false);
    }
  };

  const typeName = (types || []).find((t) => String(t.id) === String(typeId))?.name;

  return (
    <Overlay onClose={submitting ? undefined : onClose}>
      <h3 style={titleStyle}>Book an appointment</h3>
      {error && <div style={errStyle}>{error}</div>}

      {!client && (
        <div>
          <input
            style={inputStyle}
            placeholder="Search clients…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {clients === null ? (
            <div style={mutedStyle}>Loading clients…</div>
          ) : (
            filtered.map((c) => (
              <button type="button" key={c.id} style={menuRowStyle} onClick={() => pickClient(c)}>
                {c.clientName}
              </button>
            ))
          )}
          {clients !== null && filtered.length === 0 && <div style={mutedStyle}>No matching clients.</div>}
        </div>
      )}

      {client && !picked && (
        <div>
          <div style={{ fontSize: 14, marginBottom: 8 }}>
            <strong>{client.clientName}</strong>{' '}
            <button type="button" style={{ ...btnGhost, padding: '2px 8px', fontSize: 11 }} onClick={() => { setClient(null); setPicked(null); setTypeId(''); }}>change</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <input style={inputStyle} placeholder="Email (required)" value={email} onChange={(e) => setEmail(e.target.value)} />
            <input style={inputStyle} placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <select style={inputStyle} value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            <option value="">Choose appointment type…</option>
            {(types || []).map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.duration} min)</option>
            ))}
          </select>
          {typeId && email.trim() ? (
            <div style={{ marginTop: 10 }}>
              <SlotPicker calendarId={calendarId} typeId={typeId} onPick={setPicked} />
            </div>
          ) : (
            <div style={mutedStyle}>
              {!typeId && !email.trim() ? 'Pick a type and enter an email to see open times.'
                : !typeId ? 'Pick a type to see open times.'
                : 'Enter an email to see open times.'}
            </div>
          )}
        </div>
      )}

      {client && picked && (
        <div>
          <div style={{ fontSize: 14, marginBottom: 12 }}>
            Book <strong>{client.clientName}</strong> — {typeName} — <strong>{fmtFull(picked.time)}</strong>?
            They'll receive Acuity's confirmation email at {email.trim()}.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={btnPrimary} disabled={submitting} onClick={submit}>
              {submitting ? 'Booking…' : 'Confirm booking'}
            </button>
            <button type="button" style={btnGhost} disabled={submitting} onClick={() => setPicked(null)}>
              Pick another time
            </button>
          </div>
        </div>
      )}
    </Overlay>
  );
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const boxStyle = { background: 'var(--white)', borderRadius: 14, padding: 20, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' };
export const titleStyle = { margin: '0 0 8px', fontSize: 16 };
export const mutedStyle = { color: 'var(--text-muted)', fontSize: 13, padding: '8px 0' };
export const errStyle = { background: '#f8d7da', color: '#721c24', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 13 };
// NOTE: App.css sets a global `button { color: #fff }` — every button style
// here that uses a light background MUST set its own color explicitly.
const pagerBtn = { background: 'var(--surface-muted)', color: 'var(--text-strong)', border: '1px solid var(--border-color)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 13 };
const chipOff = { background: 'var(--surface-muted)', border: '1px solid var(--border-color)', borderRadius: 999, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--text-strong)' };
const chipOn = { ...chipOff, background: 'var(--primary-color)', color: '#fff', border: '1px solid var(--primary-color)' };
const btnPrimary = { background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
const btnDanger = { background: 'var(--error-color)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
const btnGhost = { background: 'transparent', color: 'var(--text-strong)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '9px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)', fontSize: 13, marginBottom: 4 };
const menuRowStyle = { display: 'block', width: '100%', textAlign: 'left', background: 'var(--surface-muted)', color: 'var(--text-strong)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer', marginTop: 6 };
